/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useState } from "react";
import CodeEntry from "../datatypeComponents/CodeEntry";
import MarkdownEntry from "../datatypeComponents/MarkdownEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import PeriodEntry, { displayStringFromFHIRPeriod } from "../datatypeComponents/PeriodEntry";
import RelativeTimeEntry, { displayStringFromFHIRRelativeTime } from "../datatypeComponents/RelativeTimeEntry";
import RangeEntry, { displayStringFromFHIRRange } from "../datatypeComponents/RangeEntry";
import QuantityEntry, { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import UriEntry, { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import ExpressionEntry, { DisplayFromFHIRExpression } from "../datatypeComponents/ExpressionEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import BooleanEntry from "../datatypeComponents/BooleanEntry";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import CodeableReferenceEntry from "../datatypeComponents/CodeableReferenceEntry";
import { useDispatch, useSelector } from "react-redux";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import ExpandToAddOrEdit from "./ExpandToAddOrEdit";

const evidenceVariableDefinitionConceptSystemChoices = [
    { 'uri': 'http://snomed.info/sct', 'display': 'SNOMED CT' },
    { 'uri': 'http://hl7.org/fhir/sid/icd-10', 'display': 'ICD-10' },
    { 'uri': 'http://loinc.org', 'display': 'LOINC' },
    { 'uri': 'http://www.nlm.nih.gov/research/umls/rxnorm', 'display': 'RxNorm' },
    { 'uri': 'https://www.whocc.no/atc_ddd_index/', 'display': 'ATC' },
    { 'uri': 'https://www.nlm.nih.gov/mesh', 'display': 'MeSH' }
];
const evidenceVariableDotHandlingValues = ['boolean', 'continuous', 'dichotomous', 'ordinal', 'polychotomous', 'extension'];
const evidenceVariableCategoryDotValueAllowedDatatypes = ['CodeableConcept', 'Quantity', 'Range', 'Reference'];

const EvidenceVariableCategoryEntry = memo(({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed }) => {
    let startingVariableCategory = {
        name: "", valueCodeableConcept: "", valueQuantity: "", valueRange: "", valueReference: ""
    }
    let startingEvidenceVariableCategoryDotValueDatatype = 'none';
    if (!startingValue) {
        startingValue = "";
    } else {
        if (startingValue.extension) { startingVariableCategory.extension = startingValue.extension; }
        if (startingValue.name) { startingVariableCategory.name = startingValue.name; }
        if (startingValue.valueCodeableConcept) {
            startingVariableCategory.valueCodeableConcept = startingValue.valueCodeableConcept;
            startingEvidenceVariableCategoryDotValueDatatype = "CodeableConcept";
        }
        if (startingValue.valueQuantity) {
            startingVariableCategory.valueQuantity = startingValue.valueQuantity;
            startingEvidenceVariableCategoryDotValueDatatype = "Quantity";
        }
        if (startingValue.valueRange) {
            startingVariableCategory.valueRange = startingValue.valueRange;
            startingEvidenceVariableCategoryDotValueDatatype = "Range";
        }
        if (startingValue.valueReference) {
            startingVariableCategory.valueReference = startingValue.valueReference;
            startingEvidenceVariableCategoryDotValueDatatype = "Reference";
        }
    }

    const [evidenceVariableCategoryState, setEvidenceVariableCategoryState] = useState(JSON.parse(JSON.stringify(startingVariableCategory || {})));
    const [evidenceVariableCategoryDotValueDatatypeState, setEvidenceVariableCategoryDotValueDatatypeState] = useState(startingEvidenceVariableCategoryDotValueDatatype);
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect((() => {
        if (evidenceVariableCategoryDotValueDatatypeState === 'CodeableConcept') {
            setEvidenceVariableCategoryState(prevState => { return { ...prevState, 'valueQuantity': null, 'valueRange': null, 'valueReference': null } })
        }
        if (evidenceVariableCategoryDotValueDatatypeState === 'Quantity') {
            setEvidenceVariableCategoryState(prevState => { return { ...prevState, 'valueCodeableConcept': null, 'valueRange': null, 'valueReference': null } })
        }
        if (evidenceVariableCategoryDotValueDatatypeState === 'Range') {
            setEvidenceVariableCategoryState(prevState => { return { ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueReference': null } })
        }
        if (evidenceVariableCategoryDotValueDatatypeState === 'Reference') {
            setEvidenceVariableCategoryState(prevState => { return { ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueRange': null } })
        }
    }), [evidenceVariableCategoryDotValueDatatypeState]);

    useEffect((() => {
        if (Object.keys(evidenceVariableCategoryState).length > 0) {
            let newEvidenceVariableCategory = {};
            if (evidenceVariableCategoryState.extension) { newEvidenceVariableCategory.extension = evidenceVariableCategoryState.extension; }
            if (evidenceVariableCategoryState.name) { newEvidenceVariableCategory.name = evidenceVariableCategoryState.name; }
            if (evidenceVariableCategoryDotValueDatatypeState === 'CodeableConcept' && evidenceVariableCategoryState.valueCodeableConcept && Object.keys(evidenceVariableCategoryState.valueCodeableConcept).length > 0) {
                newEvidenceVariableCategory.valueCodeableConcept = evidenceVariableCategoryState.valueCodeableConcept;
            }
            if (evidenceVariableCategoryDotValueDatatypeState === 'Quantity' && evidenceVariableCategoryState.valueQuantity && Object.keys(evidenceVariableCategoryState.valueQuantity).length > 0) {
                newEvidenceVariableCategory.valueQuantity = evidenceVariableCategoryState.valueQuantity;
            }
            if (evidenceVariableCategoryDotValueDatatypeState === 'Range' && evidenceVariableCategoryState.valueRange && Object.keys(evidenceVariableCategoryState.valueRange).length > 0) {
                newEvidenceVariableCategory.valueRange = evidenceVariableCategoryState.valueRange;
            }
            if (evidenceVariableCategoryDotValueDatatypeState === 'Reference' && evidenceVariableCategoryState.valueReference && Object.keys(evidenceVariableCategoryState.valueReference).length > 0) {
                newEvidenceVariableCategory.valueReference = evidenceVariableCategoryState.valueReference;
            }
            if (Object.keys(newEvidenceVariableCategory).length === 0) {
                newEvidenceVariableCategory = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newEvidenceVariableCategory }; });
        }
    }), [evidenceVariableCategoryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {(startingValue.name && typeof startingValue.name === "string") && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.valueCodeableConcept && <span><b>Value: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.valueCodeableConcept} /></span>}
                {startingValue.valueQuantity && <span> <b>Value: </b>{displayStringFromFHIRQuantity(startingValue.valueQuantity)}</span>}
                {startingValue.valueRange && <span><b>Value: </b>{displayStringFromFHIRRange(startingValue.valueRange)}</span>}
                {startingValue.valueReference && <span><b>Value: </b><DisplayFromFHIRReference reference={startingValue.valueReference} /></span >}
            </div >}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div >
    } else {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Category Name'
                    startingValue={evidenceVariableCategoryState.name} setResourceState={setEvidenceVariableCategoryState} />
                <DatatypeSelector elementXName='value[x]' allowedDatatypes={evidenceVariableCategoryDotValueAllowedDatatypes}
                    datatypeState={evidenceVariableCategoryDotValueDatatypeState} setDatatypeState={setEvidenceVariableCategoryDotValueDatatypeState} />
                {evidenceVariableCategoryDotValueDatatypeState === 'CodeableConcept' &&
                    <CodeableConceptEntry elementName='valueCodeableConcept'
                        fieldLabel='Category Definition as CodeableConcept'
                        startingValue={evidenceVariableCategoryState.valueCodeableConcept}
                        systemChoices={evidenceVariableDefinitionConceptSystemChoices} systemChoicesOpen={true}
                        setResourceState={setEvidenceVariableCategoryState} />}
                {evidenceVariableCategoryDotValueDatatypeState === 'Quantity' &&
                    <QuantityEntry elementName='valueQuantity' fieldLabel='Category Definition as Quantity'
                        startingValue={evidenceVariableCategoryState.valueQuantity}
                        setResourceState={setEvidenceVariableCategoryState} />}
                {evidenceVariableCategoryDotValueDatatypeState === 'Range' &&
                    <RangeEntry elementName='valueRange' fieldLabel='Category Definition as Range'
                        startingValue={evidenceVariableCategoryState.valueRange}
                        setResourceState={setEvidenceVariableCategoryState} />}
                {evidenceVariableCategoryDotValueDatatypeState === 'Reference' &&
                    <ReferenceEntry elementName='valueReference' fieldLabel='Category Definition as Reference'
                        startingValue={evidenceVariableCategoryState.valueReference} enableCreation={true}
                        referencedResourceTypes={["Group"]}
                        setResourceState={setEvidenceVariableCategoryState} />}
            </div>
        </>
    }
});

const EvidenceVariableDefinitionModifierDotValueAllowedDatatypes = ['CodeableConcept', 'boolean',
    'Quantity', 'Range', 'Period', 'RelativeTime', 'Reference', 'Expression', 'uri'];
const EvidenceVariableDefinitionModifierEntry = memo(({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed }) => {
    let startingDefinitionModifier = {
        code: "", valueCodeableConcept: "", valueBoolean: null, valueQuantity: "", valueRange: "",
        valuePeriod: "", valueRelativeTime: "", valueReference: "", valueExpression: "", valueUri: ""
    }
    let startingEvidenceVariableDefinitionModifierDotValueDatatype = 'none';
    if (!startingValue) {
        startingValue = "";
    } else {
        if (startingValue.extension) { startingDefinitionModifier.extension = startingValue.extension; }
        if (startingValue.code) { startingDefinitionModifier.code = startingValue.code; }
        if (startingValue.valueCodeableConcept) {
            startingDefinitionModifier.valueCodeableConcept = startingValue.valueCodeableConcept;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "CodeableConcept";
        }
        if (typeof startingValue.valueBoolean === "boolean") {
            startingDefinitionModifier.valueBoolean = startingValue.valueBoolean;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "boolean";
        }
        if (startingValue.valueQuantity) {
            startingDefinitionModifier.valueQuantity = startingValue.valueQuantity;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "Quantity";
        }
        if (startingValue.valueRange) {
            startingDefinitionModifier.valueRange = startingValue.valueRange;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "Range";
        }
        if (startingValue.valuePeriod) {
            startingDefinitionModifier.valuePeriod = startingValue.valuePeriod;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "Period";
        }
        if (startingValue.valueRelativeTime) {
            startingDefinitionModifier.valueRelativeTime = startingValue.valueRelativeTime;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "RelativeTime";
        }
        if (startingValue.valueReference) {
            startingDefinitionModifier.valueReference = startingValue.valueReference;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "Reference";
        }
        if (startingValue.valueExpression) {
            startingDefinitionModifier.valueExpression = startingValue.valueExpression;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "Expression";
        }
        if (startingValue.valueUri) {
            startingDefinitionModifier.valueUri = startingValue.valueUri;
            startingEvidenceVariableDefinitionModifierDotValueDatatype = "uri";
        }
    }

    const [evidenceVariableDefinitionModifierState, setEvidenceVariableDefinitionModifierState] = useState(JSON.parse(JSON.stringify(startingDefinitionModifier || {})));
    const [evidenceVariableDefinitionModifierDotValueDatatypeState, setEvidenceVariableDefinitionModifierDotValueDatatypeState] = useState(startingEvidenceVariableDefinitionModifierDotValueDatatype);
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect((() => {
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'CodeableConcept') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueRange': null, 'valueReference': null,
                    "valueBoolean": null, "valuePeriod": null, "valueRelativeTime": null, "valueExpression": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Quantity') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueCodeableConcept': null, 'valueRange': null, 'valueReference': null,
                    "valueBoolean": null, "valuePeriod": null, "valueRelativeTime": null, "valueExpression": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Range') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueReference': null,
                    "valueBoolean": null, "valuePeriod": null, "valueRelativeTime": null, "valueExpression": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Reference') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueRange': null,
                    "valueBoolean": null, "valuePeriod": null, "valueRelativeTime": null, "valueExpression": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'boolean') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueRange': null,
                    "valueReference": null, "valuePeriod": null, "valueRelativeTime": null, "valueExpression": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Period') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueRange': null,
                    "valueBoolean": null, "valueReference": null, "valueRelativeTime": null, "valueExpression": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'RelativeTime') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueRange': null,
                    "valueBoolean": null, "valuePeriod": null, "valueReference": null, "valueExpression": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Expression') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueRange': null,
                    "valueBoolean": null, "valuePeriod": null, "valueRelativeTime": null, "valueReference": null,
                    "valueUri": null
                }
            })
        }
        if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'uri') {
            setEvidenceVariableDefinitionModifierState(prevState => {
                return {
                    ...prevState, 'valueQuantity': null, 'valueCodeableConcept': null, 'valueRange': null,
                    "valueBoolean": null, "valuePeriod": null, "valueRelativeTime": null, "valueExpression": null,
                    "valueReference": null
                }
            })
        }
    }), [evidenceVariableDefinitionModifierDotValueDatatypeState]);

    useEffect((() => {
        if (Object.keys(evidenceVariableDefinitionModifierState).length > 0) {
            let newEvidenceVariableDefinitionModdifier = {};
            if (evidenceVariableDefinitionModifierState.extension) { newEvidenceVariableDefinitionModdifier.extension = evidenceVariableDefinitionModifierState.extension; }
            if (evidenceVariableDefinitionModifierState.code) { newEvidenceVariableDefinitionModdifier.code = evidenceVariableDefinitionModifierState.code; }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'CodeableConcept' &&
                evidenceVariableDefinitionModifierState.valueCodeableConcept &&
                Object.keys(evidenceVariableDefinitionModifierState.valueCodeableConcept).length > 0) {
                newEvidenceVariableDefinitionModdifier.valueCodeableConcept = evidenceVariableDefinitionModifierState.valueCodeableConcept;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'boolean' &&
                (evidenceVariableDefinitionModifierState.valueBoolean === true || evidenceVariableDefinitionModifierState.valueBoolean === false)) {
                newEvidenceVariableDefinitionModdifier.valueBoolean = evidenceVariableDefinitionModifierState.valueBoolean;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Quantity' &&
                evidenceVariableDefinitionModifierState.valueQuantity &&
                Object.keys(evidenceVariableDefinitionModifierState.valueQuantity).length > 0) {
                newEvidenceVariableDefinitionModdifier.valueQuantity = evidenceVariableDefinitionModifierState.valueQuantity;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Range' &&
                evidenceVariableDefinitionModifierState.valueRange &&
                Object.keys(evidenceVariableDefinitionModifierState.valueRange).length > 0) {
                newEvidenceVariableDefinitionModdifier.valueRange = evidenceVariableDefinitionModifierState.valueRange;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Period' &&
                evidenceVariableDefinitionModifierState.valuePeriod &&
                Object.keys(evidenceVariableDefinitionModifierState.valuePeriod).length > 0) {
                newEvidenceVariableDefinitionModdifier.valuePeriod = evidenceVariableDefinitionModifierState.valuePeriod;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'RelativeTime' &&
                evidenceVariableDefinitionModifierState.valueRelativeTime &&
                Object.keys(evidenceVariableDefinitionModifierState.valueRelativeTime).length > 0) {
                newEvidenceVariableDefinitionModdifier.valueRelativeTime = evidenceVariableDefinitionModifierState.valueRelativeTime;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Reference' &&
                evidenceVariableDefinitionModifierState.valueReference &&
                Object.keys(evidenceVariableDefinitionModifierState.valueReference).length > 0) {
                newEvidenceVariableDefinitionModdifier.valueReference = evidenceVariableDefinitionModifierState.valueReference;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'Expression' &&
                evidenceVariableDefinitionModifierState.valueExpression &&
                Object.keys(evidenceVariableDefinitionModifierState.valueExpression).length > 0) {
                newEvidenceVariableDefinitionModdifier.valueExpression = evidenceVariableDefinitionModifierState.valueExpression;
            }
            if (evidenceVariableDefinitionModifierDotValueDatatypeState === 'uri' &&
                evidenceVariableDefinitionModifierState.valueUri) {
                newEvidenceVariableDefinitionModdifier.valueUri = evidenceVariableDefinitionModifierState.valueUri;
            }
            if (Object.keys(newEvidenceVariableDefinitionModdifier).length === 0) {
                newEvidenceVariableDefinitionModdifier = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newEvidenceVariableDefinitionModdifier }; });
        }
    }), [evidenceVariableDefinitionModifierState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.code && <span><b>Code: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.code} /></span>}
                {(startingValue.valueCodeableConcept && typeof startingValue.valueCodeableConcept === "object") &&
                    <span><b>Value as CodeableConcept: </b>
                        <DisplayFromFHIRCodeableConcept codeableConcept={startingValue.valueCodeableConcept} /></span>}
                {(startingValue.valueQuantity && typeof startingValue.valueQuantity === "object") &&
                    <span><b>Value as Quantity: </b>{displayStringFromFHIRQuantity(startingValue.valueQuantity)}</span>}
                {(startingValue.valueRange && typeof startingValue.valueRange === "object") &&
                    <span><b>Value as Range: </b>{displayStringFromFHIRRange(startingValue.valueRange)}</span>}
                {(startingValue.valuePeriod && typeof startingValue.valuePeriod === "object") &&
                    <span><b>Value as Period: </b>{displayStringFromFHIRPeriod(startingValue.valuePeriod)}</span>}
                {(startingValue.valueRelativeTime && typeof startingValue.valueRelativeTime === "object") &&
                    <span><b>Value as RelativeTime: </b>{displayStringFromFHIRRelativeTime(startingValue.valueRelativeTime)}</span>}
                {(startingValue.valueReference && typeof startingValue.valueReference === "object") &&
                    <span><b>Value as Reference: </b>
                        <DisplayFromFHIRReference reference={startingValue.valueReference} /></span>}
                {(startingValue.valueExpression && typeof startingValue.valueExpression === "object") &&
                    <span><b>Value as Expression: </b>
                        <DisplayFromFHIRExpression expression={startingValue.valueExpression} /></span>}
                {(startingValue.valueBoolean === true) && <p><b>Value as Boolean: </b>TRUE</p>}
                {(startingValue.valueBoolean === false) && <p><b>Value as Boolean: </b>FALSE</p>}
                {(startingValue.valueUri && typeof startingValue.valueUri === "string") &&
                    <span><b>Value as URI: </b>
                        <DisplayFromFHIRUri uri={startingValue.valueUri} /></span>}
            </div >}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div >
    } else {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='code' fieldLabel='Definition Modifier Code'
                    startingValue={evidenceVariableDefinitionModifierState.code}
                    setResourceState={setEvidenceVariableDefinitionModifierState} />
                <DatatypeSelector elementXName='value[x]'
                    allowedDatatypes={EvidenceVariableDefinitionModifierDotValueAllowedDatatypes}
                    datatypeState={evidenceVariableDefinitionModifierDotValueDatatypeState}
                    setDatatypeState={setEvidenceVariableDefinitionModifierDotValueDatatypeState} />
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'CodeableConcept' &&
                    <CodeableConceptEntry elementName='valueCodeableConcept'
                        fieldLabel='Definition Modifier Value as CodeableConcept'
                        startingValue={evidenceVariableDefinitionModifierState.valueCodeableConcept}
                        systemChoices={evidenceVariableDefinitionConceptSystemChoices} systemChoicesOpen={true}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'boolean' &&
                    <BooleanEntry elementName='valueBoolean'
                        fieldLabel='Definition Modifier Value as Boolean'
                        startingValue={evidenceVariableDefinitionModifierState.valueBoolean}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'Quantity' &&
                    <QuantityEntry elementName='valueQuantity'
                        fieldLabel='Definition Modifier Value as Quantity'
                        startingValue={evidenceVariableDefinitionModifierState.valueQuantity}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'Range' &&
                    <RangeEntry elementName='valueRange'
                        fieldLabel='Definition Modifier Value as Range'
                        startingValue={evidenceVariableDefinitionModifierState.valueRange}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'Period' &&
                    <PeriodEntry elementName='valuePeriod'
                        fieldLabel='Definition Modifier Value as Period'
                        startingValue={evidenceVariableDefinitionModifierState.valuePeriod}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'RelativeTime' &&
                    <RelativeTimeEntry elementName='valueRelativeTime'
                        fieldLabel='Definition Modifier Value as RelativeTime'
                        startingValue={evidenceVariableDefinitionModifierState.valueRelativeTime}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'Reference' &&
                    <ReferenceEntry elementName='valueReference'
                        fieldLabel='Definition Modifier Value as Reference'
                        startingValue={evidenceVariableDefinitionModifierState.valueReference} enableCreation={true}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'Expression' &&
                    <ExpressionEntry elementName='valueExpression'
                        fieldLabel='Definition Modifier Value as Expression'
                        startingValue={evidenceVariableDefinitionModifierState.valueExpression}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
                {evidenceVariableDefinitionModifierDotValueDatatypeState === 'uri' &&
                    <UriEntry elementName='valueUri'
                        fieldLabel='Definition Modifier Value as URI'
                        startingValue={evidenceVariableDefinitionModifierState.valueUri}
                        setResourceState={setEvidenceVariableDefinitionModifierState} />}
            </div>
        </>
    }
});

const EvidenceVariableResourceEdit = memo(({ foi }) => {

    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
    let initialResourceJson = newResourceDetail.resourceJson;
    let resourceJson = newResourceDetail.revisedResourceJson;

    const [editResourceState, setEditResourceState] = useState({
        title: resourceJson.title,
        shortTitle: resourceJson.shortTitle,
        name: resourceJson.name,
        description: resourceJson.description,
        definition: resourceJson.definition,
        definitionModifier: resourceJson.definitionModifier,
        handling: resourceJson.handling,
        category: resourceJson.category,
        relatesTo: resourceJson.relatesTo
    });

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = editResourceState.title;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            if (editResourceState.title !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.title)) {
                console.log("resourceUpdate for EvidenceVariable.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "string",
                    "title",
                    previousInitialValue,
                    editResourceState.title);
            }
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.shortTitle;
            previousFinalValue = newResourceDetail.revisedResourceJson.shortTitle;
            newResourceDetail.revisedResourceJson.shortTitle = editResourceState.shortTitle;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            if (editResourceState.shortTitle !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.shortTitle)) {
                console.log("resourceUpdate for EvidenceVariable.shortTitle");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "string",
                    "shortTitle",
                    previousInitialValue,
                    editResourceState.shortTitle);
            }
        }
    }, [editResourceState.shortTitle]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = editResourceState.name;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.shortTitle || editResourceState.name;
            if (editResourceState.name !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.name)) {
                console.log("resourceUpdate for EvidenceVariable.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "string",
                    "name",
                    previousInitialValue,
                    editResourceState.name);
            }
        }
    }, [editResourceState.name]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.description;
            previousFinalValue = newResourceDetail.revisedResourceJson.description;
            newResourceDetail.revisedResourceJson.description = editResourceState.description;
            if (editResourceState.description !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.description)) {
                console.log("resourceUpdate for EvidenceVariable.description");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "markdown",
                    "description",
                    previousInitialValue,
                    editResourceState.description);
            }
        }
    }, [editResourceState.description]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.definition;
            previousFinalValue = newResourceDetail.revisedResourceJson.definition;
            newResourceDetail.revisedResourceJson.definition = editResourceState.definition;
            if (editResourceState.definition !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.definition)) {
                console.log("resourceUpdate for EvidenceVariable.definition");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "CodeableReference",
                    "definition",
                    previousInitialValue,
                    editResourceState.definition);
            }
        }
    }, [editResourceState.definition]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.definitionModifier;
            previousFinalValue = newResourceDetail.revisedResourceJson.definitionModifier;
            newResourceDetail.revisedResourceJson.definitionModifier = editResourceState.definitionModifier;
            if (editResourceState.definitionModifier !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.definitionModifier)) {
                console.log("resourceUpdate for EvidenceVariable.definitionModifier");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "ArrayOfEvidenceVariableDefinitionModifier",
                    "definitionModifier",
                    previousInitialValue,
                    editResourceState.definitionModifier);
            }
        }
    }, [editResourceState.definitionModifier]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.handling;
            previousFinalValue = newResourceDetail.revisedResourceJson.handling;
            newResourceDetail.revisedResourceJson.handling = editResourceState.handling;
            if (editResourceState.handling !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.handling)) {
                console.log("resourceUpdate for EvidenceVariable.handling");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "code",
                    "handling",
                    previousInitialValue,
                    editResourceState.handling);
            }
        }
    }, [editResourceState.handling]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.category;
            previousFinalValue = newResourceDetail.revisedResourceJson.category;
            newResourceDetail.revisedResourceJson.category = editResourceState.category;
            if (editResourceState.category !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.category)) {
                console.log("resourceUpdate for EvidenceVariable.category");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "ArrayOfEvidenceVariableCategory",
                    "category",
                    previousInitialValue,
                    editResourceState.category);
            }
        }
    }, [editResourceState.category]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.relatesTo;
            previousFinalValue = newResourceDetail.revisedResourceJson.relatesTo;
            newResourceDetail.revisedResourceJson.relatesTo = editResourceState.relatesTo;
            if (editResourceState.relatesTo !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.relatesTo)) {
                console.log("resourceUpdate for EvidenceVariable.relatesTo");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "ArrayOfRelatesTo",
                    "relatesTo",
                    previousInitialValue,
                    editResourceState.relatesTo);
            }
        }
    }, [editResourceState.relatesTo]);

    return <div style={{ marginLeft: "24px" }}>
        {editResourceState && <>
            <StringEntry elementName='title'
                fieldLabel={'Title (Human-friendly Label for Variable Name)'}
                startingValue={editResourceState.title || ""}
                required={(resourceJson?.description || resourceJson?.name || resourceJson?.definition || resourceJson?.handling || resourceJson?.category)}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='shortTitle'
                fieldLabel={'Short Title (for Variable Name)'}
                startingValue={editResourceState.shortTitle || ""}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='name'
                fieldLabel={'Name (Machine Label for Variable Name)'}
                startingValue={editResourceState.name || ""}
                setResourceState={setEditResourceState} />
            <MarkdownEntry elementName='description' fieldLabel={'Description'}
                startingValue={editResourceState.description || ""}
                setResourceState={setEditResourceState} />
            <CodeableReferenceEntry elementName='definition'
                fieldLabel='Variable Definition'
                referencedResourceTypes={['Group']}
                enableCreation={true} systemChoices={evidenceVariableDefinitionConceptSystemChoices}
                systemChoicesOpen={true}
                startingValue={editResourceState.definition} setResourceState={setEditResourceState} />
            <ArrayEntry datatype='EvidenceVariableDefinitionModifier' elementName='definitionModifier'
                fieldLabel='Definition Modifier' startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.definitionModifier} setResourceState={setEditResourceState} />
            <CodeEntry elementName='handling' fieldLabel='Handling'
                startingValue={editResourceState.handling} setResourceState={setEditResourceState}
                allowedValues={evidenceVariableDotHandlingValues} />
            <ArrayEntry datatype='EvidenceVariableCategory' elementName='category'
                fieldLabel='Category' startEmptyArrayClosed={true} deletable={true} deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.category}
                setResourceState={setEditResourceState} />
            <ArrayEntry datatype='RelatesTo' elementName='relatesTo'
                fieldLabel='Related Item' startEmptyArrayClosed={true} deletable={true}
                deletableArray={true} startCollapsed={true}
                startingValue={editResourceState.relatesTo}
                setResourceState={setEditResourceState} />
        </>}
    </div>
})

export default EvidenceVariableResourceEdit;
export { EvidenceVariableCategoryEntry, EvidenceVariableDefinitionModifierEntry };